import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

export default function AutoLoanPage() {
    const [price, setPrice] = useState(32000);
    const [down, setDown] = useState(4000);
    const [tradeIn, setTradeIn] = useState(0);
    const [tax, setTax] = useState(6.5);
    const [fees, setFees] = useState(400);
    const [rate, setRate] = useState(7.2);
    const [years, setYears] = useState(5);

    const { loan, monthly, totalInterest, totalPaid } = useMemo(() => {
        const p = toNum(price);
        const salesTax = p * (toNum(tax) / 100);
        const financed = Math.max(0, p - toNum(down) - toNum(tradeIn) + salesTax + toNum(fees));
        const m = monthlyPayment(financed, toNum(rate), Math.max(0.5, toNum(years)));
        const totalPaid = m * toNum(years) * 12;
        return { loan: financed, monthly: m, totalInterest: totalPaid - financed, totalPaid };
    }, [price, down, tradeIn, tax, fees, rate, years]);

    return (
        <CalculatorShell
            slug="auto-loan"
            explainer={
                                <>
                    <h2>How this auto loan calculator works</h2>
                    <p>
                        An auto loan calculator figures out your monthly car payment and total interest,
                        accounting for vehicle price, down payment, trade-in value, sales tax, and loan terms.
                        Use it to know your real number before you sit down with a dealer.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Financed amount:</strong> calculated as price minus down payment minus trade-in value plus applicable sales tax.</li>
                        <li><strong>Monthly payment:</strong> derived from the standard amortization formula applied to the financed amount.</li>
                        <li><strong>Sales tax:</strong> varies by state and can add thousands to what you actually finance.</li>
                        <li><strong>Trade-in value:</strong> reduces the amount you finance, directly lowering your payment.</li>
                        <li><strong>Loan term:</strong> longer terms lower monthly payments but significantly increase total interest paid.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Get pre-approved from your bank or credit union before visiting the dealership — it gives you negotiating power.</li>
                        <li>Keep your loan term at 60 months or less to avoid being underwater on the vehicle.</li>
                        <li>Factor total ownership cost — insurance, maintenance, fuel — not just the monthly payment.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Vehicle price" prefix="$" value={price} onChange={setPrice} dataTestId="calc-input-price" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Down payment" prefix="$" value={down} onChange={setDown} dataTestId="calc-input-down" />
                        <Field label="Trade-in value" prefix="$" value={tradeIn} onChange={setTradeIn} dataTestId="calc-input-tradein" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Sales tax" suffix="%" value={tax} onChange={setTax} step={0.1} dataTestId="calc-input-tax" />
                        <Field label="Fees" prefix="$" value={fees} onChange={setFees} dataTestId="calc-input-fees" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="APR" suffix="%" value={rate} onChange={setRate} step={0.1} dataTestId="calc-input-rate" />
                        <Field label="Term (years)" value={years} onChange={setYears} dataTestId="calc-input-years" />
                    </div>
                    <div className="pt-2 text-xs text-muted-foreground font-mono tabular">
                        Amount financed: {fmtUSD(loan)}
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Monthly payment"
                        value={monthly}
                        format={(n) => fmtUSD(n)}
                        secondary={[
                            { label: "Total paid", value: totalPaid, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalpaid" },
                            { label: "Interest", value: totalInterest, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalinterest" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
